import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Read the signed-in user's profile (RLS-scoped). */
export const getMyProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      console.warn("[getMyProfile]", error.message);
      return { profile: null as null };
    }
    return { profile: data };
  });

const profilePatch = z.object({
  display_name: z.string().trim().min(1).max(120).optional(),
  avatar_url: z.string().url().max(500).nullable().optional(),
  language: z.enum(["ES", "EN", "PT"]).optional(),
  favorite_team: z.string().trim().max(80).nullable().optional(),
  palette: z.string().trim().max(80).nullable().optional(),
  tenant_slug: z.string().trim().max(80).nullable().optional(),
  onboarding_progress: z.record(z.string(), z.unknown()).optional(),
  attribution: z.record(z.string(), z.unknown()).optional(),
});

export const updateMyProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => profilePatch.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("profiles")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update(data as any)
      .eq("id", userId);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

const guestMigrateSchema = z.object({
  language: z.string().max(8).optional(),
  favorite_team: z.string().max(80).optional(),
  palette: z.string().max(80).optional(),
  onboarding_progress: z.record(z.string(), z.unknown()).optional(),
  attribution: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Merge guest-mode preferences into the authenticated user's profile.
 * Empty fields on the patch are ignored; non-empty fields overwrite.
 */
export const migrateGuestData = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => guestMigrateSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const patch: Record<string, unknown> = {};
    if (data.language) patch.language = data.language.toUpperCase();
    if (data.favorite_team) patch.favorite_team = data.favorite_team;
    if (data.palette) patch.palette = data.palette;
    if (data.onboarding_progress && Object.keys(data.onboarding_progress).length) {
      patch.onboarding_progress = data.onboarding_progress;
    }
    if (data.attribution && Object.keys(data.attribution).length) {
      patch.attribution = data.attribution;
    }
    if (!Object.keys(patch).length) return { ok: true as const, migrated: 0 };
    const { error } = await supabase
      .from("profiles")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update(patch as any)
      .eq("id", userId);
    if (error) {
      console.warn("[migrateGuestData]", error.message);
      return { ok: false as const, migrated: 0 };
    }
    return { ok: true as const, migrated: Object.keys(patch).length };
  });